import React, { useEffect, useMemo, useRef, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import {
  loadGoogleMaps,
  GoogleMapsLoaderError,
} from '../../../services/googleMapsLoader';
import {
  AdminOrderRow,
  AdminOrdersResponse,
  RoutePhase,
  RouteStopDTO,
} from '../../../models/APIModels';
import Button from '../../Button';

interface RouteMapProps {
  /** Stops to render, in current route order. Caller pre-sorts by `routePosition`. */
  stops: RouteStopDTO[];
  /** ISO date for the day being viewed; used to fetch matching admin orders. */
  date: string;
  /** Currently scoped partner — required to fetch order coordinates. */
  partnerId: number | null;
  /** True when the user can edit / remove a stop or trigger optimise. */
  canControlLogistics: boolean;
  /** Triggered when the user requests an optimise (re-uses backend recompute). */
  onOptimise?: () => void;
  /** Optional remove-stop callback wired to the backend (no-op when omitted). */
  onRemoveStop?: (stop: RouteStopDTO) => void;
  /** Day-locked flag — disables edit / remove affordances inside the InfoWindow. */
  isLocked: boolean;
}

interface ResolvedStop {
  stop: RouteStopDTO;
  order?: AdminOrderRow;
  /** Lat/lng resolved from the order's postcode via Google Geocoder. */
  position?: google.maps.LatLngLiteral;
  /** Geocoder failure flag — kept so we can render a "no location" badge. */
  geocodeFailed?: boolean;
}

const PIN_COLOUR_COLLECTION = '#604b8d'; // --kkUltraViolet
const PIN_COLOUR_DELIVERY = '#008dc9'; // --RoyalBlue

function pinSvg(label: number, phase: RoutePhase): string {
  const fill = phase === 'COLLECTION' ? PIN_COLOUR_COLLECTION : PIN_COLOUR_DELIVERY;
  // Small numbered map pin. Inline SVG keeps the bundle clean (no extra
  // package). 28×40 viewBox; tip anchor sits at the bottom centre.
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">` +
    `<path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" ` +
    `fill="${fill}" stroke="#fff" stroke-width="2"/>` +
    `<text x="14" y="19" text-anchor="middle" font-family="Inter, sans-serif" ` +
    `font-size="13" font-weight="700" fill="#fff">${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function formatEtaWindow(start?: string, end?: string): string {
  if (start && end) return `${start}–${end}`;
  if (start) return start;
  return '';
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Map-shaped Route Plan view. Lazy-loads the Google Maps JS API on mount,
 * geocodes each stop's postcode via the Geocoder service, and renders
 * numbered pins (1, 2, 3, …) in current route order with a polyline between
 * them. Click a pin → InfoWindow with stop details and (role-gated) edit /
 * remove affordances.
 *
 * Stops carry no lat/lng on the wire today (`RouteStopDTO`), so we fetch
 * `/logistics/admin/orders` for the day and match orderId → postcode →
 * geocoded coordinates. This keeps the map self-contained without a backend
 * shape change.
 */
export default function RouteMap({
  stops,
  date,
  partnerId,
  canControlLogistics,
  onOptimise,
  onRemoveStop,
  isLocked,
}: RouteMapProps) {
  const mapNodeRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [resolvedStops, setResolvedStops] = useState<ResolvedStop[]>([]);

  const reduceMotion = useMemo(prefersReducedMotion, []);

  // --- 1. Lazy-load Google Maps ---------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setLoadError(undefined);
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadState('error');
        if (err instanceof GoogleMapsLoaderError && err.code === 'MISSING_KEY') {
          setLoadError(
            'Map view unavailable. Configure REACT_APP_GOOGLE_MAPS_BROWSER_KEY to enable.',
          );
        } else if (err instanceof GoogleMapsLoaderError) {
          setLoadError(`Map view failed to load (${err.code}). Try again shortly.`);
        } else {
          setLoadError('Map view failed to load. Try again shortly.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // --- 2. Fetch admin orders for the day so we can resolve postcodes --
  useEffect(() => {
    if (partnerId == null || stops.length === 0) {
      setOrders([]);
      return;
    }
    let cancelled = false;
    axiosKK
      .get<AdminOrdersResponse>('/logistics/admin/orders', {
        params: { partnerId, fromDate: date, toDate: date, pageSize: 500, page: 0 },
      })
      .then(({ data }) => {
        if (!cancelled) setOrders(data.orders || []);
      })
      .catch(() => {
        if (!cancelled) setOrders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId, date, stops.length]);

  // --- 3. Geocode each stop's postcode --------------------------------
  useEffect(() => {
    if (loadState !== 'ready' || stops.length === 0) {
      setResolvedStops([]);
      return;
    }
    let cancelled = false;
    const geocoder = new google.maps.Geocoder();
    const orderById = new Map(orders.map((o) => [o.id, o]));

    const promises = stops.map(async (stop): Promise<ResolvedStop> => {
      const order = orderById.get(stop.orderId);
      if (!order || !order.postcode) {
        return { stop, order, geocodeFailed: true };
      }
      try {
        const result = await geocoder.geocode({
          address: order.postcode,
          componentRestrictions: { country: 'GB' },
        });
        const first = result.results[0];
        if (!first) return { stop, order, geocodeFailed: true };
        const loc = first.geometry.location;
        return { stop, order, position: { lat: loc.lat(), lng: loc.lng() } };
      } catch {
        return { stop, order, geocodeFailed: true };
      }
    });

    Promise.all(promises).then((resolved) => {
      if (!cancelled) setResolvedStops(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [loadState, stops, orders]);

  // --- 4. Render the map + markers + polyline -------------------------
  useEffect(() => {
    if (loadState !== 'ready' || !mapNodeRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapNodeRef.current, {
        // Centre over the UK by default; fitBounds takes over once we have stops.
        center: { lat: 52.5, lng: -1.5 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
      });
      infoWindowRef.current = new google.maps.InfoWindow();
    }
    const map = mapRef.current;
    const infoWindow = infoWindowRef.current!;

    // Clear previous markers + polyline.
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    const located = resolvedStops.filter(
      (r): r is ResolvedStop & { position: google.maps.LatLngLiteral } => Boolean(r.position),
    );
    if (located.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    located.forEach((entry, index) => {
      const marker = new google.maps.Marker({
        position: entry.position,
        map,
        title: `Stop ${index + 1} · Order #${entry.stop.orderId}`,
        icon: {
          url: pinSvg(index + 1, entry.stop.phase),
          // Anchor the SVG tip on the geographic point.
          anchor: new google.maps.Point(14, 40),
          scaledSize: new google.maps.Size(28, 40),
        },
      });
      marker.addListener('click', () => {
        const order = entry.order;
        const eta = formatEtaWindow(entry.stop.etaStart, entry.stop.etaEnd);
        const safeAddress = order?.address ? escapeHtml(order.address) : '';
        const safePostcode = order?.postcode ? escapeHtml(order.postcode) : '';
        const phaseLabel = entry.stop.phase === 'COLLECTION' ? 'Collection' : 'Delivery';
        const removeDisabled = !canControlLogistics || isLocked;
        const removeButton = onRemoveStop
          ? `<button type="button" class="route-map-info__action" ` +
            `data-action="remove" ` +
            (removeDisabled ? `disabled aria-disabled="true" ` : '') +
            `aria-label="Remove stop ${index + 1}">Remove stop</button>`
          : '';
        const html =
          `<div class="route-map-info">` +
          `<div class="route-map-info__title">Stop ${index + 1} · Order #${entry.stop.orderId}</div>` +
          `<div class="route-map-info__meta">${escapeHtml(phaseLabel)}` +
          (eta ? ` · ETA ${escapeHtml(eta)}` : '') +
          `</div>` +
          (safeAddress ? `<div class="route-map-info__address">${safeAddress}</div>` : '') +
          (safePostcode ? `<div class="route-map-info__postcode">${safePostcode}</div>` : '') +
          (entry.stop.overridden
            ? `<div class="route-map-info__badge">Manually reordered</div>`
            : '') +
          `<div class="route-map-info__actions">${removeButton}</div>` +
          `</div>`;
        infoWindow.setContent(html);
        infoWindow.open({ map, anchor: marker });
        // Wire up the remove button after the DOM lands.
        google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
          const node = document.querySelector(
            '.route-map-info__action[data-action="remove"]',
          ) as HTMLButtonElement | null;
          if (node && !node.disabled && onRemoveStop) {
            node.addEventListener(
              'click',
              () => {
                infoWindow.close();
                onRemoveStop(entry.stop);
              },
              { once: true },
            );
          }
        });
      });
      markersRef.current.push(marker);
      bounds.extend(entry.position);
    });

    // Polyline in current route order — straight-line, geodesic so it bends
    // gently across the UK rather than drawing a flat rhumb line.
    polylineRef.current = new google.maps.Polyline({
      path: located.map((e) => e.position),
      geodesic: true,
      strokeColor: PIN_COLOUR_COLLECTION,
      strokeOpacity: 0.85,
      strokeWeight: 3,
      map,
    });

    if (located.length === 1) {
      map.setCenter(located[0].position);
      map.setZoom(13);
    } else {
      map.fitBounds(bounds, 64);
      if (reduceMotion) {
        // fitBounds animates by default; with reduced-motion we snap by
        // re-setting the centre/zoom synchronously after the bounds settle.
        google.maps.event.addListenerOnce(map, 'idle', () => {
          const c = map.getCenter();
          if (c) map.setCenter(c);
        });
      }
    }
  }, [loadState, resolvedStops, canControlLogistics, isLocked, onRemoveStop, reduceMotion]);

  // --- 5. Render -------------------------------------------------------
  if (loadState === 'error') {
    return (
      <div className="route-map route-map--unavailable" role="status">
        {loadError ?? 'Map view unavailable.'}
      </div>
    );
  }

  const unresolvedCount = resolvedStops.filter((r) => r.geocodeFailed).length;

  return (
    <div className="route-map">
      <div className="route-map__toolbar">
        <Button
          variant="secondary"
          size="sm"
          className="route-map__optimise"
          onClick={onOptimise}
          disabled={!onOptimise || !canControlLogistics || isLocked}
          aria-label="Optimise route"
          title={
            isLocked
              ? 'Day is locked. Unlock the day to optimise.'
              : !canControlLogistics
                ? 'You do not have permission to optimise this route.'
                : undefined
          }
        >
          Optimise route
        </Button>
        {unresolvedCount > 0 && (
          <span className="route-map__warning" role="status">
            {unresolvedCount} stop{unresolvedCount === 1 ? '' : 's'} could not be located on the map.
          </span>
        )}
      </div>
      {loadState !== 'ready' && (
        <div className="route-map__loading" role="status">
          Loading map…
        </div>
      )}
      <div ref={mapNodeRef} className="route-map__canvas" aria-label="Route plan map" />
    </div>
  );
}
